import { Dialog, DialogTitle, Checkbox, FormControlLabel } from '@material-ui/core';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { RootState } from '../../../../redux/root';
import {
  setFormData,
  setSelectedInspection,
  setSelectedInspectionFormMarkup,
  setShowPopup,
} from '../../../../redux/slices/inspectionsSlice';
import { INFORMATION_REQUIRED_STATUSES, labels, REPORT_REQUIRED_STATUSES, reportFingerprint } from './constants';
import useLoadInspectionEditor from './useLoadInspectionEditor';
import ActivityTitle from './Controls/ActivityTitle';
import ActivityStatus from './Controls/ActivityStatus';
import Contact from './Controls/Contacts';
import ReportRequiredMessage from './Controls/ReportRequiredMessage';
import InspectionCompleteConfirmation from './MessagePopups/InspectionCompleteConfirmation';
import MaskedPhone from './Controls/MaskedPhone';
import InspectionTabs from './Controls/InspectionTabs';
import StaticField from './Controls/StaticField';
import InspectionType from './Controls/InspectionType';
import DatePicker from './Controls/DatePicker';
import Period from './Controls/Period';
import Information from './Controls/Information';
import ReportCreator from './Controls/ReportCreator';
import CommentsChat from './Controls/CommentsChat';
import InspectionSaving from './MessagePopups/InspectionSaving';
import { closeInspection, getFieldsForInspection, persistInspectionData } from '../Utils';
import ContactsPopup from './MessagePopups/ContactsPopup';
import { updatePermitInspection } from '../../../../api/inspections';
import { InspectionStatuses } from '../../../../constants';
import FillThresholdReport from '../../../../organisms/ThresholdReports/FillThresholdReport';
import useFormMarkupTools from '../../../../organisms/ThresholdReports/useFormMarkupTools';
import { applyStatusToResult } from '../../../../organisms/ThresholdReports/fillLayout';
import GateCode from './Controls/GateCode';
import ApprovedPlans from './Controls/ApprovedPlans';
import AVOperator from './Controls/AVOperator';
import { Loader } from '../../../../atoms';

const InspectionEditor: FC<{ loadData?: Function }> = ({ loadData }) => {
  const modalOpen = useSelector((s: RootState) => s.inspections.modalOpen);
  const { selectedInspection, showPopup, disabledGlobalSearch } = useSelector((s: RootState) => s.inspections);
  const permitUsers = useSelector((s: RootState) => s.inspections.permitUsers);
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  // Whether this inspection has a builder form (threshold / specialty /
  // jurisdiction). Reported by InspectionTabs, which does the matching.
  const [hasForm, setHasForm] = useState(false);
  const detailsPageRef = useRef<HTMLDivElement | null>(null);
  const [reportPdf, setReportPdf] = useState<any>();
  const [pdfObject, setPdfObject] = useState<any>();
  const [inspectionReport, setInspectionReport] = useState<{ name?: string }>({});
  const [disabledSubmit, setDisabledSubmit] = useState(true);
  const [originalStatusName, setOriginalStatusName] = useState('');
  const [manager, setManager] = useState('');
  // Why Update is disabled for want of a report; empty when it is not.
  const [reportError, setReportError] = useState('');
  const showReportError = !!reportError;
  // A save that failed. Kept apart from reportError, which disables Update.
  const [saveError, setSaveError] = useState('');
  // What the created report was drawn from -- see reportFingerprint.
  const [createdReportFingerprint, setCreatedReportFingerprint] = useState<string>();
  const { updateFormMarkupOnInspection } = useFormMarkupTools();
  const [infoExpanded, setInfoExpanded] = useState<boolean>(() => {
    const stored = localStorage.getItem('inspectionsInfoBlock.expanded');
    return stored === null ? false : stored === 'true';
  });
  const toggleInfo = () => {
    const next = !infoExpanded;
    setInfoExpanded(next);
    localStorage.setItem('inspectionsInfoBlock.expanded', String(next));
  };

  const toDateUTC = selectedInspection.id ? new Date(selectedInspection.inspectionDate) : '';
  const followUpUTC = selectedInspection.id ? new Date(selectedInspection?.followUp) : '';
  const { register, handleSubmit, errors, setError, clearErrors, control, setValue, watch, getValues } = useForm({
    defaultValues: {
      ...selectedInspection,
      toDate: toDateUTC,
      followUp: followUpUTC,
      isRequestedAtf: selectedInspection?.isRequestedAtf || false,
      clientonsite: selectedInspection?.is_clientonsite ?? selectedInspection?.isClientonsite ?? selectedInspection?.isClientOnSite ?? selectedInspection?.clientOnSite ?? false,
    },
  });
  useEffect(() => {
  if (isRemoteInspection) return;

    setValue('clientonsite', false, { shouldDirty: true, shouldValidate: false });
    setValue('avoperator', null, { shouldDirty: true, shouldValidate: false });
  }, [isRemoteInspection, setValue]);
  const selectedTitle = watch('activityTitle');
  const activityTitleName =
  typeof selectedTitle === 'string'
    ? selectedTitle
    : selectedTitle?.name || selectedTitle?.label || selectedTitle?.title || '';

  const isRemoteInspection = activityTitleName?.trim()?.toLowerCase() === 'remote inspection';
  const information = watch('information');
  const activityStatus: { name: string } = watch('activityStatus');
  useEffect(() => {
    if (selectedInspection.managerFirstName) {
      setManager(`${selectedInspection.managerFirstName} ${selectedInspection.managerLastName}`);
    }
  }, [selectedInspection]);

  // A report created in this edit counts only while what it printed still
  // matches the form.
  const reportIsCurrent =
    !!inspectionReport?.name &&
    createdReportFingerprint === reportFingerprint(activityStatus?.name, information);
  // Only a current report is ever uploaded: a stale one could still reach the
  // server under a status that needs none (e.g. created as Pass, then changed
  // back to Scheduled).
  const currentReportPdf = reportIsCurrent ? reportPdf : undefined;

  // Pdf.tsx hands the created report back through this. The fingerprint is the
  // one from the render the Create Report click came from, i.e. what the report
  // was drawn with.
  const onReportCreated = file => {
    setInspectionReport(file);
    setCreatedReportFingerprint(reportFingerprint(activityStatus?.name, information));
  };

  useEffect(() => {
    if (activityStatus) {
      clearErrors('information');
      setReportError('');

      const statusName = activityStatus.name;
      // Set below on the first run, so read it from the record until then.
      const original = originalStatusName || selectedInspection.statusName;
      let blocked = false;

      // `information` comes straight from the API and is null on plenty of
      // inspections -- an unguarded .trim() here white-screened the popup.
      if (INFORMATION_REQUIRED_STATUSES.includes(statusName) && !information?.trim()) {
        blocked = true;
        setError('information', { message: '*' });
      }
      if (REPORT_REQUIRED_STATUSES.includes(statusName)) {
        // A report already on the inspection was made for the status it has
        // now, so it stops counting once the status changes.
        const reportOnFile = !!selectedInspection.report && statusName === original;
        if (inspectionReport?.name && !reportIsCurrent) {
          blocked = true;
          setReportError('The report no longer matches this inspection. Create the report again before saving.');
        } else if (!reportIsCurrent && !reportOnFile) {
          blocked = true;
          setReportError(`Create the inspection report before saving with status ${statusName}.`);
        }
      }
      // One decision for both rules. It used to be set per rule, so a report
      // being present re-enabled Update over missing Information.
      setDisabledSubmit(blocked);

      if (!originalStatusName) {
        setOriginalStatusName(selectedInspection.statusName);
      }
      dispatch(
        setSelectedInspection({
          ...selectedInspection,
          statusName: activityStatus.name,
        }),
      );
    }
  }, [activityStatus, information, inspectionReport, createdReportFingerprint]);

  // Activity Status is the ONE place the inspector records the outcome; the
  // form's Results row follows it (fillLayout.applyStatusToResult) and is
  // read-only in the form. Re-run when the markup is replaced as well —
  // loading and the template rebase both swap it wholesale.
  //
  // Nothing is saved from here: the markup rides in the Update payload
  // (getFieldsForInspection sends formMarkup) together with the status it
  // came from, so closing without Update leaves both as they were. Form
  // autosave sends the Result for the SAVED status (savedStatusName below),
  // never the picked one. Declared
  // AFTER the effect above, and patching only formMarkup, so that effect's
  // whole-record dispatch cannot undo it.
  const formMarkup = selectedInspection.formMarkup;
  const activityStatusName = activityStatus?.name;
  useEffect(() => {
    if (!activityStatusName || !formMarkup) return;
    const synced = applyStatusToResult(formMarkup, activityStatusName);
    if (synced !== formMarkup) dispatch(setSelectedInspectionFormMarkup(synced));
  }, [activityStatusName, formMarkup]);

  const { isLoading } = useLoadInspectionEditor({ selectedTitle });

  const onSubmit = async (data: any) => {
    if (originalStatusName !== selectedInspection.statusName) {
      dispatch(setFormData(data));
      if (data.activityStatus.name === InspectionStatuses.SCHEDULED) {
        dispatch(setShowPopup('saving'));
      } else {
        dispatch(setShowPopup('confirmation'));
      }
    } else {
      const payload = getFieldsForInspection(selectedInspection, data);
      // Same status, but a report may have been created for it -- e.g. adding
      // the missing report to an inspection already marked Pass. This path
      // used to drop it. Uploaded first, as in InspectionSaving.
      setSaveError('');
      try {
        if (currentReportPdf) {
          await persistInspectionData(selectedInspection, currentReportPdf);
        }
        await updatePermitInspection(payload);
      } catch (e) {
        setSaveError('The inspection could not be saved. Try again.');
        return;
      }
      await updateFormMarkupOnInspection(selectedInspection.formMarkup);
      closeInspection(dispatch);
      if (loadData) {
        loadData();
      }
    }
  };
  // Every validated field lives in the details at the top of the Details tab,
  // but both tabs stay mounted (TabPage just collapses to height 0), and the
  // form below the details is long. If the inspector hits Update from Comments
  // or from deep in the form, react-hook-form blocks the submit and renders the
  // messages out of sight — the button would look dead. Bring them back to the
  // fields that actually have the problem.
  const onInvalid = () => {
    setTab(0);
    if (detailsPageRef.current) detailsPageRef.current.scrollTop = 0;
  };

  return (
    <div>
      <Dialog open={modalOpen} fullWidth maxWidth="lg" keepMounted aria-describedby="alert-dialog-slide-description">
        <PopupWrapper>
          {showPopup === 'confirmation' && <InspectionCompleteConfirmation />}
          {showPopup === 'saving' && <InspectionSaving reportPdf={currentReportPdf} pdfObject={pdfObject} />}
          {showPopup === 'contacts' && <ContactsPopup />}
          {/* Covers the whole editor until the dropdown/contact/report data this
              form depends on has arrived — before that the fields render empty
              and look like the inspection has no data. */}
          {isLoading && (
            <LoadingOverlay>
              <Loader />
              <LoadingText>Loading inspection…</LoadingText>
            </LoadingOverlay>
          )}
          <InspectionTabs value={tab} setValue={setTab} watch={watch} onFormAvailable={setHasForm} />
          <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <StyledDialogTitle disableTypography>Update Inspection</StyledDialogTitle>
            <DialogContent>
              {/* Details and the inspection form share ONE scrollable page: the
                  details on top, the form (when this inspection has one)
                  below. The form used to be its own "Inspection Notes" tab. */}
              <TabPage tab={tab} index={0} ref={detailsPageRef}>
                <ControlsWrapper>
                  <InfoToggleRow>
                    <InfoToggleButton type="button" onClick={toggleInfo} aria-expanded={infoExpanded}>
                      <InfoChevron $open={infoExpanded}>▾</InfoChevron>
                      {infoExpanded ? 'Hide details' : 'Show details'}
                    </InfoToggleButton>
                  </InfoToggleRow>
                  {infoExpanded && (
                    <InfoCard>
                      <StaticField label="Project Name" value={selectedInspection.projectName} />
                      <StaticField label="Permit Name" value={selectedInspection.permitName} />
                      <StaticField label="Permit Number" value={selectedInspection.permitNumber} />
                      <StaticField label="Permit Address" value={selectedInspection.projectAddress} />
                      <StaticField label="Jurisdiction" value={selectedInspection.projectJurisdiction} />
                      <StaticField label="Project Manager" value={manager} />
                      <StaticField label="Inspection Number" value={selectedInspection.id} />
                    </InfoCard>
                  )}
                  <EditableControlsWrapper>
                    <ActivityTitle control={control} errors={errors} setValue={setValue} />
                    <InspectionType
                      getValues={getValues}
                      setDisabledSubmit={setDisabledSubmit}
                      control={control}
                      errors={errors}
                      setValue={setValue}
                      watch={watch}
                    />

                    <DatePicker name="toDate" control={control} errors={errors} label={labels.inspectionDate} />
                    <div>
                      <Period control={control} errors={errors} setValue={setValue} />
                    </div>
                    <Information register={register} errors={errors} setDisableSubmit={setDisabledSubmit} />
                    <ActivityStatus control={control} setValue={setValue} watch={watch} setDisableSubmit={setDisabledSubmit} />
                    <Contact control={control} errors={errors} setValue={setValue} />
                    <MaskedPhone control={control} errors={errors} name="cellPhone" setValue={setValue} />
                    <MaskedPhone control={control} errors={errors} name="phoneNumber" setValue={setValue} />
                    <GateCode register={register} errors={errors} />
                    <ApprovedPlans />
                    {isRemoteInspection ? (
                      <AVOperator control={control} errors={errors} setValue={setValue} />
                    ) : null}
                    <div style={{ gridColumn: '2 / 3', marginTop: '10px' }}>
                      <Controller
                        name="isRequestedAtf"
                        control={control}
                        render={({ value, onChange }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!value}
                                onChange={e => onChange(e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Requested After The Fact?"
                          />
                        )}
                      />
                    </div>
                  </EditableControlsWrapper>
                  <ReportCreator
                    setPdfObject={setPdfObject}
                    setReportPdf={setReportPdf}
                    activityStatus={activityStatus}
                    setInspectionReport={onReportCreated}
                    getValues={getValues}
                    watch={watch}
                  />
                  <ReportRequiredMessage message={reportError || saveError} />
                </ControlsWrapper>
                {hasForm && (
                  <FormSection>
                    <FormSectionTitle>Inspection Form</FormSectionTitle>
                    <FillThresholdReport
                      mainTab={tab}
                      // Read from the record until the status effect has
                      // captured it — selectedInspection.statusName tracks
                      // the picker from then on.
                      savedStatusName={originalStatusName || selectedInspection.statusName}
                    />
                  </FormSection>
                )}
              </TabPage>
              <TabPage tab={tab} index={1}>
                <CommentsWrapper>
                  <CommentsChat noFile title="Comments List" buttonText="Add Comment" />
                </CommentsWrapper>
              </TabPage>
            </DialogContent>
            <StyledDialogActions>
              {/* The inspection form autosaves; only the details above it are
                  discarded by Cancel. The form's hint says so. */}
              <SecondaryButton type="button" onClick={() => closeInspection(dispatch)}>
                Cancel
              </SecondaryButton>
              {/* The modal's only submit. The form autosaves, but the details
                  do NOT — Update is what saves them and runs the completion
                  flow, so it stays visible on every tab. */}
              <PrimaryButton
                disabled={isLoading || showReportError || disabledSubmit || disabledGlobalSearch}
                type="submit"
              >
                Update
              </PrimaryButton>
            </StyledDialogActions>
          </Form>
        </PopupWrapper>
      </Dialog>
    </div>
  );
};

export default InspectionEditor;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
`;

const LoadingText = styled.div`
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--neutral-600);
`;

const DoubleRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 32px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 4px 0 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--border);
`;

const InfoToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const InfoToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const InfoChevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  color: var(--neutral-500);
`;

const StyledDialogTitle = styled(DialogTitle)`
  && {
    padding: 20px 24px 8px;
    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--mainText);
    border-bottom: 1px solid var(--border);
    margin-bottom: 4px;

    @media only screen and (max-width: 840px) {
      padding: 14px 14px 6px;
      font-size: 1rem;
    }
  }
`;

const StyledDialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--border);
  background: var(--neutral-0);

  @media only screen and (max-width: 840px) {
    padding: 12px 14px;
  }

  @media only screen and (max-width: 540px) {
    padding: 10px;

    button {
      flex: 1;
      min-width: 0;
    }
  }
`;

const PrimaryButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-500);
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  min-width: 110px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const PopupWrapper = styled.div`
  position: relative;
  height: 90vh;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  .MuiDialogTitle-root {
    padding: 0 24px;
  }

  @media only screen and (max-width: 840px) {
    height: calc(100vh - 40px);

    .MuiDialogTitle-root {
      padding: 0 14px;
    }
  }

  @media only screen and (max-width: 540px) {
    height: 100vh;
    height: 100dvh;
  }
`;
const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  gap: 12px;
  .react-select__menu {
    height: 120px;
    overflow: auto;
  }

  @media only screen and (max-width: 840px) {
    padding: 14px;
  }

  @media only screen and (max-width: 540px) {
    padding: 10px;
  }
`;

const EditableControlsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 32px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const DialogContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

const TabPage = styled.div<{ tab: number; index: number }>`
  flex-grow: 1;
  width: 100%;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  height: ${({ tab, index }) => (tab === index ? '70vh' : '0')};
`;

// The inspection form, below the details on the same page.
const FormSection = styled.section`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border-top: 1px solid var(--border);
`;

const FormSectionTitle = styled.h3`
  margin: 0;
  padding: 20px 24px 0;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);

  @media only screen and (max-width: 840px) {
    padding: 14px 14px 0;
  }

  @media only screen and (max-width: 540px) {
    padding: 10px 10px 0;
  }
`;

const CommentsWrapper = styled.div`
  position: relative;
  top: 0;
  margin: 26px;

  @media only screen and (max-width: 840px) {
    margin: 14px;
  }

  @media only screen and (max-width: 540px) {
    margin: 10px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;
